/**
 * V82: 직원별 상세 휴가 사용 내역 조회 훅
 *
 * BE: getEmployeeLeaveUsageDetails callable
 */

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';

export interface LeaveUsageDetailItem {
  id: string;
  source: 'request' | 'import' | 'manual_adjustment';
  sourceLabel: string;
  leaveTypeCode: string;
  leaveTypeName: string;
  displayDate: string;
  sortKey: string;
  unit: 'fullDay' | 'halfDay' | 'morningHalf' | 'afternoonHalf' | 'hourly' | 'aggregate';
  amount: number;
  amountLabel: string;
  status?: 'approved' | 'cancelPending' | 'cancelOrgApproved' | 'rejected';
  statusLabel?: string;
  reason?: string;
  createdAt: string;
  requestId?: string;
  _raw?: {
    startDate?: string;
    endDate?: string;
    targetMonth?: string;
    totalDays?: number;
  };
}

export interface UsageDetailsResponse {
  success: boolean;
  items: LeaveUsageDetailItem[];
  totalCount: number;
  summary: {
    totalApprovedDays: number;
    totalImportedDays: number;
    totalAdjustedDays: number;
    // V82.2
    totalCompensatoryHours: number;
    compensatoryLabel: string;
  };
}

interface Input {
  employeeId: string;
  year?: number;
}

export function useEmployeeLeaveUsageDetails(employeeId: string | null, year?: number) {
  const [items, setItems] = useState<LeaveUsageDetailItem[]>([]);
  const [summary, setSummary] = useState<UsageDetailsResponse['summary']>({
    totalApprovedDays: 0,
    totalImportedDays: 0,
    totalAdjustedDays: 0,
    totalCompensatoryHours: 0,
    compensatoryLabel: '대체 0시간',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!employeeId) {
      setItems([]);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const functions = getFunctions();
      const call = httpsCallable<Input, UsageDetailsResponse>(functions, 'getEmployeeLeaveUsageDetails');
      const payload: Input = { employeeId };
      if (year) payload.year = year;
      const result = await call(payload);
      if (!result.data.success) {
        throw new Error('조회 실패');
      }
      setItems(result.data.items || []);
      setSummary(result.data.summary);
    } catch (err: any) {
      console.error('[V82] getEmployeeLeaveUsageDetails error:', err);
      setError(new Error(err.message || '상세 조회 중 오류가 발생했습니다.'));
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [employeeId, year]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { items, summary, isLoading, error, refetch: fetch };
}
